import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Stack
} from '@mui/material';
import { 
  Add as AddIcon, 
  Delete as DeleteIcon,
  LocalOffer as PackageIcon 
} from '@mui/icons-material';
import DataTable from '../components/common/DataTable';
import { NumberTextField } from '../components/common';
import { servicePackageService } from '../services/servicePackageService';
import { useTranslation } from 'react-i18next';

const ServicePackages: React.FC = () => {
  const { t } = useTranslation();
  const [packages, setPackages] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentPackage, setCurrentPackage] = useState<any>(null);

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    validityDays: 365,
    termsAndConditions: '',
    services: [] as any[],
    isActive: true
  });

  const [newService, setNewService] = useState({
    name: '',
    code: '',
    allowedCount: 1,
    extraKmPrice: 0
  });

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      const response = await servicePackageService.getAll();
      // Handle Strapi v4 response format: { data: [...], meta: {...} }
      const packagesData = response?.data || response || [];
      setPackages(Array.isArray(packagesData) ? packagesData : []);
    } catch (error) {
      console.error('Error fetching packages:', error);
      setPackages([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (pkg: any = null) => {
    if (pkg) {
      // Handle Strapi v4 format (with attributes) or plain format
      const pkgData = pkg.attributes || pkg;
      setCurrentPackage(pkg);
      setFormData({
        name: pkgData.name || pkg.name || '',
        code: pkgData.code || pkg.code || '',
        description: pkgData.description || pkg.description || '',
        validityDays: pkgData.validityDays || pkg.validityDays || 365,
        termsAndConditions: pkgData.termsAndConditions || pkg.termsAndConditions || '',
        services: pkgData.services || pkg.services || [],
        isActive: pkgData.isActive !== undefined ? pkgData.isActive : (pkg.isActive !== undefined ? pkg.isActive : true)
      });
    } else {
      setCurrentPackage(null);
      setFormData({
        name: '',
        code: '',
        description: '',
        validityDays: 365,
        termsAndConditions: '',
        services: [],
        isActive: true
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentPackage(null);
    setNewService({ name: '', code: '', allowedCount: 1, extraKmPrice: 0 });
  };

  const handleAddService = () => {
    if (newService.name && newService.code) {
      setFormData({
        ...formData,
        services: [...formData.services, { ...newService }]
      });
      setNewService({ name: '', code: '', allowedCount: 1, extraKmPrice: 0 });
    }
  };

  const handleRemoveService = (index: number) => {
    const updatedServices = formData.services.filter((_, i) => i !== index);
    setFormData({ ...formData, services: updatedServices });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (currentPackage) {
        // Handle Strapi v4 format - get ID from either format
        const packageId = currentPackage.id || currentPackage.attributes?.id;
        await servicePackageService.update(packageId, formData);
      } else {
        await servicePackageService.create(formData);
      }
      handleCloseDialog();
      fetchPackages();
    } catch (error) {
      console.error('Error saving package:', error);
      alert('Error saving package: ' + (error as any).message);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm(t('confirmDelete'))) {
      try {
        await servicePackageService.delete(id);
        fetchPackages();
      } catch (error) {
        console.error('Error deleting package:', error);
      }
    }
  };

  const columns = [
    { 
      field: 'name', 
      headerName: t('name'),
      flex: 1
    },
    { 
      field: 'code', 
      headerName: t('code'),
      width: 100
    },
    { 
      field: 'validityDays', 
      headerName: 'Validity (Days)',
      width: 130
    },
    { 
      field: 'services', 
      headerName: 'Services',
      flex: 1,
      renderCell: (params: any) => {
        const services = params.value || [];
        return (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {services.slice(0, 3).map((service: any, idx: number) => (
              <Chip 
                key={idx} 
                label={`${service.name} (${service.allowedCount}x)`} 
                size="small"
                color="primary"
                variant="outlined"
              />
            ))}
            {services.length > 3 && (
              <Chip label={`+${services.length - 3} more`} size="small" />
            )}
          </Box>
        );
      }
    },
    { 
      field: 'isActive', 
      headerName: t('status'),
      width: 100,
      renderCell: (params: any) => (
        <Chip 
          label={params.value ? t('active') : t('inactive')} 
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      )
    }
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PackageIcon color="primary" />
          <Typography variant="h4">
            Service Packages
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          {t('addPackage')}
        </Button>
      </Box>

      <Card>
        <CardContent>
          <DataTable
            columns={columns}
            rows={packages}
            loading={loading}
            onEdit={handleOpenDialog}
            onDelete={handleDelete}
          />
        </CardContent>
      </Card>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {currentPackage ? t('editPackage') : t('addPackage')}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
                  <TextField
                    fullWidth
                    label={t('name')}
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </Box>
                <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
                  <TextField
                    fullWidth
                    label={t('code')}
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    required
                  />
                </Box>
              </Box>
              <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
                <NumberTextField
                  fullWidth
                  label="Validity Days"
                  value={formData.validityDays}
                  onChange={(e) => setFormData({ ...formData, validityDays: parseInt(e.target.value) })}
                  allowDecimal={false}
                  required
                />
              </Box>
              <TextField
                fullWidth
                label={t('description')}
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
              
              {/* Services Section */}
              <Box>
                <Typography variant="h6" gutterBottom>
                  Services
                </Typography>
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                      <Box sx={{ flex: '1 1 200px' }}>
                        <TextField
                          fullWidth
                          label="Service Name"
                          value={newService.name}
                          onChange={(e) => setNewService({ ...newService, name: e.target.value })}
                        />
                      </Box>
                      <Box sx={{ flex: '1 1 150px' }}>
                        <TextField
                          fullWidth
                          label="Service Code"
                          value={newService.code}
                          onChange={(e) => setNewService({ ...newService, code: e.target.value })}
                        />
                      </Box>
                      <Box sx={{ flex: '0 1 100px' }}>
                        <NumberTextField
                          fullWidth
                          label="Count"
                          value={newService.allowedCount}
                          onChange={(e) => setNewService({ ...newService, allowedCount: parseInt(e.target.value) || 1 })}
                          inputProps={{ min: 1 }}
                          allowDecimal={false}
                        />
                      </Box>
                      <Box sx={{ flex: '0 1 120px' }}>
                        <NumberTextField
                          fullWidth
                          label="Extra KM Price (OMR)"
                          value={newService.extraKmPrice}
                          onChange={(e) => setNewService({ ...newService, extraKmPrice: parseFloat(e.target.value) || 0 })}
                          inputProps={{ min: 0, step: 0.01 }}
                        />
                      </Box>
                      <Box sx={{ flex: '0 0 auto' }}>
                        <Button 
                          variant="outlined" 
                          onClick={handleAddService}
                          sx={{ height: '56px', minWidth: '56px' }}
                        >
                          <AddIcon />
                        </Button>
                      </Box>
                    </Box>
                    
                    {formData.services.length > 0 && (
                      <Table size="small" sx={{ mt: 2 }}>
                        <TableHead>
                          <TableRow>
                            <TableCell>Service Name</TableCell>
                            <TableCell>Code</TableCell>
                            <TableCell>Allowed Count</TableCell>
                            <TableCell>Extra KM Price (OMR)</TableCell>
                            <TableCell width={50}></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {formData.services.map((service, index) => (
                            <TableRow key={index}>
                              <TableCell>{service.name}</TableCell>
                              <TableCell>{service.code}</TableCell>
                              <TableCell>{service.allowedCount}</TableCell>
                              <TableCell>{service.extraKmPrice || 0}</TableCell>
                              <TableCell>
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleRemoveService(index)}
                                  color="error"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                </Card>
              </Box>

              <TextField
                fullWidth
                label="Terms & Conditions"
                multiline
                rows={4}
                value={formData.termsAndConditions}
                onChange={(e) => setFormData({ ...formData, termsAndConditions: e.target.value })}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>{t('cancel')}</Button>
            <Button type="submit" variant="contained">
              {t('save')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default ServicePackages;

